import { useState, useEffect } from "react";
import { CheckCircle, Clock, AlertCircle, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { Progress } from "./ui/progress";

export default function DocumentScoreCard({ scoreData, documents = [], showDetails = false }) {
  const [expandedCategories, setExpandedCategories] = useState({});

  const getScoreColor = (score) => {
    if (score >= 800) return "text-emerald-600";
    if (score >= 600) return "text-blue-600";
    if (score >= 400) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 800) return "bg-emerald-500";
    if (score >= 600) return "bg-blue-500";
    if (score >= 400) return "bg-amber-500";
    return "bg-red-500";
  };

  const getScoreLabel = (score) => {
    if (score >= 800) return "Excellent";
    if (score >= 600) return "Good";
    if (score >= 400) return "Fair";
    return "Needs Work";
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  if (!scoreData) return null;

  return (
    <div className="space-y-4" data-testid="document-score-card">
      {/* Main Score Display */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Document Score</h3>
            <p className="text-sm text-slate-500">Based on {scoreData.total_documents} required documents</p>
          </div>
          <div className="text-right">
            <div className={`text-4xl font-bold ${getScoreColor(scoreData.score)}`}>
              {scoreData.score}
              <span className="text-lg text-slate-400">/1000</span>
            </div>
            <p className={`text-sm font-medium ${getScoreColor(scoreData.score)}`}>
              {getScoreLabel(scoreData.score)}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${getScoreBgColor(scoreData.score)}`}
            style={{ width: `${(scoreData.score / 1000) * 100}%` }}
          />
          {/* Score markers */}
          <div className="absolute top-0 left-[40%] w-px h-full bg-slate-300" />
          <div className="absolute top-0 left-[60%] w-px h-full bg-slate-300" />
          <div className="absolute top-0 left-[80%] w-px h-full bg-slate-300" />
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>0</span>
          <span>400</span>
          <span>600</span>
          <span>800</span>
          <span>1000</span>
        </div>

        {/* Document Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="text-center p-3 bg-emerald-50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-emerald-600">
              <CheckCircle className="w-4 h-4" />
              <span className="font-bold text-xl">{scoreData.approved_documents}</span>
            </div>
            <p className="text-xs text-emerald-600">Approved</p>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-amber-600">
              <Clock className="w-4 h-4" />
              <span className="font-bold text-xl">{scoreData.pending_documents}</span>
            </div>
            <p className="text-xs text-amber-600">Pending</p>
          </div>
          <div className="text-center p-3 bg-slate-100 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-slate-500">
              <AlertCircle className="w-4 h-4" />
              <span className="font-bold text-xl">{scoreData.missing_documents}</span>
            </div>
            <p className="text-xs text-slate-500">Missing</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Completion</span>
            <span className="font-semibold text-slate-900">{scoreData.completion_percentage}%</span>
          </div>
          <Progress value={scoreData.completion_percentage} className="h-2 mt-2" />
        </div>
      </div>

      {/* Category Breakdown */}
      {showDetails && scoreData.category_scores && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h4 className="font-semibold text-slate-900">Category Breakdown</h4>
          </div>
          <div className="divide-y divide-slate-100">
            {Object.entries(scoreData.category_scores).map(([catId, catData]) => (
              <div key={catId} className="p-4">
                <button
                  onClick={() => toggleCategory(catId)}
                  className="w-full flex items-center justify-between"
                  data-testid={`category-${catId}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">{catData.name}</p>
                      <p className="text-xs text-slate-500">
                        {catData.approved}/{catData.total} documents approved
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">{catData.score}/{catData.max_score}</p>
                      <p className="text-xs text-slate-500">points</p>
                    </div>
                    {expandedCategories[catId] ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Document List */}
                {expandedCategories[catId] && documents.length > 0 && (
                  <div className="mt-4 pl-8 space-y-2">
                    {documents
                      .find(d => d.category_id === catId)?.documents
                      .map((doc) => (
                        <div 
                          key={doc.id} 
                          className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            {doc.status === "approved" ? (
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                            ) : doc.status === "pending" ? (
                              <Clock className="w-4 h-4 text-amber-500" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-slate-300" />
                            )}
                            <span className="text-sm text-slate-700">{doc.name}</span>
                            {doc.required && (
                              <span className="text-xs text-red-500">*</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">{doc.points} pts</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              doc.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                              doc.status === "pending" ? "bg-amber-100 text-amber-700" :
                              "bg-slate-100 text-slate-500"
                            }`}>
                              {doc.status === "not_submitted" ? "Not Submitted" : doc.status}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
